import React, { useEffect, useState } from 'react';
import { DataIndicators } from '../DataIndicators';
import { useLocationListData } from '../../data/DataProviders/LocationListDataProvider';
import { sortObjectArrayNaturally } from '../../lib';
import { isDataLoaded, UserOperations } from '../../types';
import { useHistory } from 'react-router-dom';
import { isRecordFound, useSelectedOrganizationData, useUserRoles } from '../../hooks/data-hooks';
import { useCurrentAppRoute } from '../../hooks/app-route-hooks';
import { pushAppRoute } from '../../data/domain/route-manager';
import { GenericToolbar } from '../GenericToolbar';
import { ContentContainer } from '../ContentContainer';
import { RecordDataIndicators } from '../RecordDataIndicators';
import { useLanguageLocale } from '../../hooks/language-locale-hooks';
import { Box, Button, IconButton, InputAdornment, List, TextField } from '@mui/material';
import { DeviceCountProvider } from '../../data/DataProviders/DeviceCountProvider';
import AddIcon from '@mui/icons-material/Add';
import { LocationListItem } from './LocationListItem';
import { Close, Search, HelpOutline } from '@mui/icons-material';
import { isValidCharWithoutSpace } from '../../lib/site-helper';
import { EmptyState } from '@brightlayer-ui/react-components';

export const LocationList: React.FC = (): JSX.Element => {
    const { t } = useLanguageLocale();
    const history = useHistory();
    const locationsList = useLocationListData();
    const locationListData = locationsList[0];
    const isEmpty = isDataLoaded(locationListData) && locationListData.value.locations.length === 0;
    const currentAppRoute = useCurrentAppRoute();
    const selectedOrganization = useSelectedOrganizationData();
    const userRole = useUserRoles();
    const [query, setQuery] = useState('');
    const [list, setList] = useState<any[]>([]);
    const [prevOrg, setPrevOrg] = useState('');

    const searchResults = (searchString: string): any[] => {
        if (isDataLoaded(locationListData)) {
            const q = searchString.toLowerCase().trim();
            return locationListData.value.locations.filter((item: any): boolean => {
                if (
                    item.locationName
                        .toLowerCase()
                        .trim()
                        .includes(q)
                ) {
                    return true;
                }
                return false;
            });
        }
        return [];
    };
    const onChangeLocationName = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        if (isValidCharWithoutSpace(e.target.value)) {
            setQuery(e.target.value);
        }
    };
    const resetSearch = (): void => {
        setQuery('');
    };

    useEffect(() => {
        if (isRecordFound(selectedOrganization) && prevOrg !== selectedOrganization.value.name) {
            setPrevOrg(selectedOrganization.value.name);
            resetSearch();
        }
    }, [selectedOrganization]); //eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (isDataLoaded(locationListData)) {
            if (query === '') {
                setList(locationListData.value.locations);
            } else {
                setList(searchResults(query));
            }
        }
    }, [query, locationListData]); //eslint-disable-line react-hooks/exhaustive-deps

    const hasAddSite = isRecordFound(userRole)
        ? userRole.value.some(
              (x) => x.operations && x.operations.some((operation) => operation === UserOperations.AddSite)
          )
        : false;
    const NavigateToAddLocation = (): void => {
        if ('orgId' in currentAppRoute) {
            pushAppRoute(history, { type: 'AddLocation', orgId: currentAppRoute.orgId });
        }
    };

    if (!isRecordFound(selectedOrganization)) {
        return (
            <ContentContainer>
                <RecordDataIndicators
                    recordData={selectedOrganization}
                    errorMessage={t('DASHBOARD.ERROR_LOADING_ORGANIZATION')}
                    notFoundIcon="Business"
                    notFoundMessage={t('DASHBOARD.ORGANIZATION_NOT_FOUND')}
                />
            </ContentContainer>
        );
    }

    return <>
        <GenericToolbar title={'Manage Locations'} subtitle={selectedOrganization.value.name} />
        <ContentContainer>
            <DataIndicators
                errorMessage={'No Locations found'}
                emptyMessage={'No Locations found'}
                data={locationListData}
                emptyIcon="LocationCity"
                isEmpty={isEmpty}
                withAction={true}
                description={
                    'Locations represent physical addresses where your Devices are located. Add a Location as your first step in connecting your Devices.'
                }
                action={
                    hasAddSite ? (
                        <Button
                           color="primary"
                         
                            variant="contained"
                            startIcon={<AddIcon fontSize="large" style={{ color: 'white' }} />}
                            data-testid="add-location-icon"
                            onClick={(): void => {
                                NavigateToAddLocation();
                            }}
                        >
                            Add Location
                        </Button>
                    ) : (
                        <> </>
                    )
                }
            />

            {isDataLoaded(locationListData) && locationListData.value.locations.length > 0 && (
                <>
                    <Box display="flex" justifyContent="space-between">
                        <Box display="flex" justifyContent="flex-start">
                            <TextField
                                inputProps={{ 'data-testid': 'search', maxLength: 100 }}
                                value={query}
                                style={{ width: 250 }}
                                placeholder={'Search Location'}
                                onChange={(e): void => onChangeLocationName(e)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search data-testid="search-icon" />
                                        </InputAdornment>
                                    ),
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            {query !== '' && (
                                                <IconButton onClick={resetSearch} size="large">
                                                    <Close fontSize="small" />
                                                </IconButton>
                                            )}
                                        </InputAdornment>
                                    ),
                                }}
                                autoFocus
                                variant="standard"
                            />
                        </Box>
                        {hasAddSite && (
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<AddIcon fontSize="large" style={{ color: 'white' }} />}
                                data-testid="add-location-icon"
                                onClick={(): void => {
                                    NavigateToAddLocation();
                                }}
                            >
                                Add Location
                            </Button>
                        )}
                    </Box>
                    <List>
                        {sortObjectArrayNaturally(list, [{ sortField: 'locationName' }]).map((location) => (
                            <DeviceCountProvider key={location.locationId} siteId={location.locationId}>
                                <LocationListItem key={location.locationId} locationData={location} />
                            </DeviceCountProvider>
                        ))}
                    </List>
                </>
            )}
            {isDataLoaded(locationListData) && list.length === 0 && (
                <EmptyState
                    icon={
                        <div style={{ fontSize: 64 }}>
                            <HelpOutline fontSize="inherit" />
                        </div>
                    }
                    title={'No results found'}
                ></EmptyState>
            )}
        </ContentContainer>
    </>;
};
